import {
  Badge,
  Button,
  DatePicker,
  Dropdown,
  Form,
  Pagination,
  Select,
  Table,
  Tag,
  Tooltip,
} from 'antd';
import {useMemo, useState} from 'react';
import styles from './allRewardSchemes.module.scss';
import {useMutation, useQuery} from '@apollo/client';
import {toast} from 'react-toastify';
import {Link, useNavigate, useSearchParams} from 'react-router-dom';
import {
  GET_ALL_REWARD_SCHEMES,
  GET_USER_REWARDS,
} from 'graphql/queries/rewardScheme';
import DBModal from 'components/modal/modal';
import UserLink from 'components/userLink/UserLink';
import {UPDATE_USER_REWARD_STATUS} from 'graphql/mutations/rewardScheme';
import SubmitButton from 'components/Startup/components/SubmitButton';
import {
  operationTypeEnum,
  rewardStatus,
  rewardType,
  walletCurrencies,
} from 'utils/mock';
import {
  assetClassLabel,
  instrumentKeyColor,
  instrumentKeyLabel,
} from 'pages/AssetManagement/modules/instrumentKeys';
import {useSelector} from 'react-redux';
import {NavTabsEnum} from 'utils/constants';
import {makeSelectTabOperations} from 'redux/store/auth';
import filterIcon from 'icons/filter.svg';
import moment from 'moment';
import gamer from 'images/gamer.png';
import dotsIcon from 'images/dots.svg';
import {ReverseReward} from '../reverseReward/reverseReward';

// Every status the backend can hold. Anything not listed here used to render
// as "Approved", which hid failed and reversed rewards in plain sight.
const STATUS_TAG = {
  APPROVED: {color: 'green', label: 'Approved'},
  PENDING: {color: 'gold', label: 'Pending'},
  REJECTED: {color: 'red', label: 'Rejected'},
  FAILED: {color: 'volcano', label: 'Failed'},
  REVERSED: {color: 'purple', label: 'Reversed'},
  EXPIRED: {color: 'default', label: 'Expired'},
};
const ALL_STATUSES = Object.keys(STATUS_TAG);

const tabs = [
  ...ALL_STATUSES.map(value => ({label: STATUS_TAG[value].label, value})),
  {label: 'All', value: 'ALL'},
];

const formatMoney = (currency, value) =>
  value === null || value === undefined
    ? '--'
    : `${currency || ''} ${Number(value).toLocaleString()}`.trim();

// Filter form values -> query variables. Empty selections drop out so the
// backend sees no filter rather than an empty list.
const toFilterVariables = values => {
  const [from, to] = values.dateRange || [];
  return {
    rewardSchemeIds: values.rewardSchemeIds?.length
      ? values.rewardSchemeIds
      : undefined,
    rewardType: values.rewardType || undefined,
    eventType: values.eventType || undefined,
    currency: values.currency || undefined,
    userType: values.userType || undefined,
    entryMode: values.entryMode || undefined,
    mode: values.mode || undefined,
    startDate: from ? moment(from).startOf('day').toISOString() : undefined,
    endDate: to ? moment(to).endOf('day').toISOString() : undefined,
  };
};

const countActiveFilters = values =>
  Object.values(values).filter(value =>
    Array.isArray(value) ? value.length > 0 : Boolean(value),
  ).length;

const AllUserReward = ({userId}) => {
  const navigate = useNavigate();
  const [openStatusModal, setStatusModal] = useState(false);
  const [userReward, setUserReward] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchParams, setSearchParams] = useSearchParams('');
  const [activeTab, setActiveTab] = useState(
    () =>
      tabs.find(tab => tab.label === searchParams.get('tab'))?.value ||
      'APPROVED',
  );
  const [openFilterModal, setOpenFilterModal] = useState(false);
  // The applied filters, as the form submitted them. Kept in state (not sent
  // through refetch) so a tab or page change does not silently drop them.
  const [filters, setFilters] = useState({});
  const [filterForm] = Form.useForm();

  const [openReverseModal, setOpenReverseModal] = useState({
    open: false,
    id: null,
  });
  const [reverseForm] = Form.useForm();

  const tabOperations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.RewardScheme),
  );

  const {loading, refetch, data} = useQuery(GET_USER_REWARDS, {
    variables: {
      ...(activeTab === 'ALL' ? {statuses: ALL_STATUSES} : {status: activeTab}),
      ...toFilterVariables(filters),
      page: currentPage,
      limit,
      user: userId,
    },
    errorPolicy: 'all',
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    onError: error => toast.error(error?.message),
  });

  // Schemes for the filter dropdown. The list is paginated, so ask for enough
  // to cover every scheme in one go.
  const {data: schemesData} = useQuery(GET_ALL_REWARD_SCHEMES, {
    variables: {page: 1, limit: 200},
    errorPolicy: 'all',
  });
  const schemeOptions = useMemo(
    () =>
      (schemesData?.getAllRewardSchemes?.data?.rewardSchemes || []).map(
        scheme => ({
          value: scheme.id,
          label: `${scheme.title}${scheme.isActive ? '' : ' (inactive)'}`,
        }),
      ),
    [schemesData],
  );

  const activeFilterCount = countActiveFilters(filters);

  const handlePageChange = page => {
    if (page === currentPage) return;
    setCurrentPage(page);
  };

  const handleLimitChange = size => {
    if (size === limit) return;
    setLimit(size);
    setCurrentPage(1);
  };

  const applyFilters = values => {
    setCurrentPage(1);
    setFilters(values);
    setOpenFilterModal(false);
  };

  const clearFilters = () => {
    filterForm.resetFields();
    setCurrentPage(1);
    setFilters({});
    setOpenFilterModal(false);
  };

  const [adminUpdateUserRewardStatus, {loading: loadingUpdate}] = useMutation(
    UPDATE_USER_REWARD_STATUS,
  );

  const handleSave = formValues => {
    adminUpdateUserRewardStatus({
      variables: {
        adminUpdateUserRewardStatusId: userReward,
        status: formValues?.status,
      },
    }).then(({data: {adminUpdateUserRewardStatus}}) => {
      if (adminUpdateUserRewardStatus.__typename === 'Error') {
        toast.error(adminUpdateUserRewardStatus.message);
      } else {
        setStatusModal(false);
        refetch();
        toast.success('User reward updated successfully');
      }
    });
  };

  // On a user's own page, their name stays plain rather than linking to itself.
  const linkedUserId = user => (user?.id !== userId ? user?.id : undefined);

  const columns = [
    {
      title: 'User',
      dataIndex: 'user',
      key: 'user',
      render: (text, record) => (
        <div className={styles['record-label']}>
          {record?.userType === 'USER' ? (
            <div className={styles['record-name']}>
              {record?.userId?.imageUrl ? (
                <img src={record?.userId?.imageUrl} alt="" />
              ) : (
                <img src={gamer} alt="" />
              )}
              <h4>
                <UserLink userId={linkedUserId(record?.userId)}>
                  {record?.userId?.firstName} {record?.userId?.lastName}
                </UserLink>
              </h4>
            </div>
          ) : record?.userType === 'REFEREE' ? (
            <div className={styles['record-name']}>
              {record?.refereeId?.imageUrl ? (
                <img src={record?.refereeId?.imageUrl} alt="" />
              ) : (
                <img src={gamer} alt="" />
              )}
              <h4>
                <UserLink userId={linkedUserId(record?.refereeId)}>
                  {record?.refereeId?.firstName} {record?.refereeId?.lastName}
                </UserLink>
              </h4>
            </div>
          ) : (
            <div className={styles['record-name']}>
              {record?.referrerId?.imageUrl ? (
                <img src={record?.referrerId?.imageUrl} alt="" />
              ) : (
                <img src={gamer} alt="" />
              )}
              <h4>
                <UserLink userId={linkedUserId(record?.referrerId)}>
                  {record?.referrerId?.firstName} {record?.referrerId?.lastName}
                </UserLink>
              </h4>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Scheme',
      dataIndex: 'rewardScheme',
      key: 'rewardScheme',
      render: (text, record) =>
        record?.rewardScheme?.id ? (
          <div className={styles['record-label']}>
            <h4>
              <Link
                to={`/dashboard/reward-schemes/update/${record.rewardScheme.id}/EN/users`}>
                {record.rewardScheme.title}
              </Link>
            </h4>
            <Tag color="default">
              {record.rewardScheme.type?.replaceAll('_', ' ')}
            </Tag>
          </div>
        ) : (
          <div className={styles['record-label']}>
            <h4>--</h4>
          </div>
        ),
    },
    {
      title: 'Reward Type',
      dataIndex: 'rewardType',
      key: 'rewardType',
      render: (text, record) => (
        <div>
          <h4>{record?.userReward?.type?.replaceAll('_', ' ')}</h4>
        </div>
      ),
    },
    {
      title: 'Reward',
      dataIndex: 'reward',
      key: 'reward',
      render: (text, record) => (
        <div>
          <h4>
            {record?.userReward?.type === 'TRANSACTION_FEE_WAIVER'
              ? `${record?.userReward?.cashValue || '--'} ${
                  record?.userReward?.currency?.currency || '--'
                }`
              : record?.userReward?.type === 'STOCKS'
              ? // A ticker alone is no longer unique — the same symbol can
                // list on two exchanges — so name the exchange when the
                // record carries one.
                `${record?.userReward?.rewardUnits || '--'} units of ${
                  record?.userReward?.ticker
                    ? `${record.userReward.ticker}${
                        record.userReward.exchangeCode
                          ? ` · ${record.userReward.exchangeCode}`
                          : ''
                      }`
                    : 'Any'
                }`
              : `${record?.userReward?.amount || '--'} ${
                  record?.userReward?.currency?.currency || '--'
                }`}
          </h4>
        </div>
      ),
    },
    {
      title: 'User Type',
      dataIndex: 'userType',
      key: 'userType',
      render: (text, render) => (
        <div className={styles['record-label']}>
          <h4>{render?.userType}</h4>
        </div>
      ),
    },
    {
      title: 'Action Type',
      dataIndex: 'eventType',
      key: 'eventType',
      render: (text, render) => (
        <div className={styles['record-label']}>
          <h4>{render?.eventType}</h4>
        </div>
      ),
    },
    {
      // Which asset the event was on, at both levels of the scheme's scope —
      // the difference between a reward that paid on a listed stock and one
      // that paid on an IPO allocation is only visible here.
      title: 'Asset',
      dataIndex: 'eventInstrumentKey',
      key: 'eventInstrumentKey',
      render: (text, render) =>
        render?.eventAssetClassKey || render?.eventInstrumentKey ? (
          <div className={styles['record-label']}>
            <h4>{assetClassLabel(render?.eventAssetClassKey)}</h4>
            {render?.eventInstrumentKey && (
              <Tag color={instrumentKeyColor(render?.eventInstrumentKey)}>
                {instrumentKeyLabel(render?.eventInstrumentKey)}
              </Tag>
            )}
          </div>
        ) : (
          <div className={styles['record-label']}>
            <h4>--</h4>
          </div>
        ),
    },
    {
      title: 'Amount',
      dataIndex: 'eventAmount',
      key: 'eventAmount',
      render: (text, render) => (
        <div className={styles['record-label']}>
          <h4>
            {formatMoney(render?.eventCurrency?.currency, render?.eventAmount)}
          </h4>
        </div>
      ),
    },
    {
      // The fees the event carried. For a fee waiver this is the base the
      // reward was computed from, so the cap is visible next to the payout.
      title: 'Fees',
      dataIndex: 'eventFees',
      key: 'eventFees',
      render: (text, render) => (
        <div className={styles['record-label']}>
          <h4>
            {formatMoney(render?.eventCurrency?.currency, render?.eventFees)}
          </h4>
        </div>
      ),
    },
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (text, render) => (
        <div className={styles['record-label']}>
          <h4>{moment(render?.createdAt).format('DD MMM YYYY')}</h4>
          {render?.updatedAt &&
            !moment(render.updatedAt).isSame(render.createdAt, 'day') && (
              <span className={styles.dateSub}>
                Updated {moment(render.updatedAt).format('DD MMM YYYY')}
              </span>
            )}
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (text, record) => {
        const tag = STATUS_TAG[record?.status] || {
          color: 'default',
          label: record?.status || '--',
        };
        return (
          <div className={styles['record-label']}>
            <Tag color={tag.color}>{tag.label}</Tag>
          </div>
        );
      },
    },
    {
      title: 'Reversal Status',
      dataIndex: 'reversalRecord',
      key: 'reversalRecord',
      render: (text, record) =>
        record?.reversalRecord?.status ? (
          <div className={styles['record-label']}>
            <Tooltip title={record.reversalRecord.reversalReason}>
              <Tag
                color={
                  record.reversalRecord.status === 'REVERSED'
                    ? 'red'
                    : 'default'
                }>
                {record.reversalRecord.status}
              </Tag>
            </Tooltip>
          </div>
        ) : (
          <div className={styles['record-label']}>
            <h4>--</h4>
          </div>
        ),
    },
    {
      title: 'Entry Mode',
      dataIndex: 'entryMode',
      key: 'entryMode',
      render: (text, record) => (
        <div className={styles['record-label']}>
          {record?.entryMode === 'MANUAL' ? (
            <Tag color="default">Manual</Tag>
          ) : (
            <Tag color="blue">Automatic</Tag>
          )}
        </div>
      ),
    },
  ];

  if (tabOperations?.update) {
    columns.push({
      title: 'Actions',
      dataIndex: 'operation',
      key: 'actions',
      render: (text, record) => (
        <div className={styles.actions}>
          {['PENDING', 'APPROVED'].includes(record?.status) && (
            <Dropdown
              trigger="click"
              menu={{
                items: [
                  {
                    key: 1,
                    label: 'Update Status',
                    disabled: record?.status === 'APPROVED',
                    onClick: () => {
                      setStatusModal(true);
                      setUserReward(record?.id);
                    },
                  },
                  {
                    key: 2,
                    label: 'Update Reward',
                    disabled:
                      record?.entryMode === 'AUTOMATIC' ||
                      record?.status === 'APPROVED',
                    onClick: () =>
                      navigate(
                        '/dashboard/reward-schemes/update-user-reward/' +
                          record?.id,
                      ),
                  },
                  {
                    key: 3,
                    label: 'Reverse Reward',
                    disabled:
                      record?.status === 'PENDING' ||
                      record?.reversalRecord?.status === 'REVERSED',
                    onClick: () => {
                      setOpenReverseModal({open: true, id: record?.id});
                    },
                  },
                ],
              }}>
              <div>
                <img src={dotsIcon} alt="" />
              </div>
            </Dropdown>
          )}
        </div>
      ),
    });
  }

  return (
    <div className={styles['table-container']}>
      <div className={styles['details-toggler']}>
        {tabs.map(tab => (
          <span
            className={activeTab === tab.value ? styles.active : ''}
            onClick={() => {
              setCurrentPage(1);
              setActiveTab(tab.value);
              setSearchParams({tab: tab.label});
            }}
            key={tab.value}>
            {tab.label}
          </span>
        ))}
      </div>
      <div className={styles.filterSection}>
        <Badge count={activeFilterCount} size="small" offset={[-6, 6]}>
          <div
            onClick={() => setOpenFilterModal(!openFilterModal)}
            className={styles.filter}>
            <h2>Filter</h2>
            <img src={filterIcon} alt="" />
          </div>
        </Badge>
        <div
          style={{display: openFilterModal ? 'block' : 'none'}}
          className={styles.filterItems}>
          <h3>FILTER REWARDS</h3>
          <Form form={filterForm} onFinish={applyFilters} layout="vertical">
            <div className={styles.filterGrid}>
              <Form.Item
                name="rewardSchemeIds"
                label="Reward Scheme"
                className={styles.filterWide}>
                <Select
                  mode="multiple"
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  maxTagCount="responsive"
                  placeholder="Any scheme"
                  options={schemeOptions}
                />
              </Form.Item>
              <Form.Item
                name="dateRange"
                label="Granted Between"
                className={styles.filterWide}>
                <DatePicker.RangePicker
                  style={{width: '100%'}}
                  format="DD MMM YYYY"
                />
              </Form.Item>
              <Form.Item name="rewardType" label="Reward Type">
                <Select allowClear placeholder="Any">
                  {rewardType.map(({key, name}) => (
                    <Select.Option key={key} value={key}>
                      {name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name="eventType" label="Action Type">
                <Select allowClear placeholder="Any">
                  {operationTypeEnum.map(({key, name}) => (
                    <Select.Option key={key} value={key}>
                      {name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name="currency" label="Event Currency">
                <Select allowClear showSearch placeholder="Any">
                  {walletCurrencies.map(({key, name}) => (
                    <Select.Option key={key} value={key}>
                      {key} · {name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name="userType" label="User Type">
                <Select allowClear placeholder="Any">
                  <Select.Option value="REFERRER">Referrer</Select.Option>
                  <Select.Option value="REFEREE">Referee</Select.Option>
                  <Select.Option value="USER">User</Select.Option>
                </Select>
              </Form.Item>
              <Form.Item name="entryMode" label="Entry Mode">
                <Select allowClear placeholder="Any">
                  <Select.Option value="MANUAL">Manual</Select.Option>
                  <Select.Option value="AUTOMATIC">Automatic</Select.Option>
                </Select>
              </Form.Item>
              <Form.Item name="mode" label="Mode">
                <Select allowClear placeholder="Any">
                  <Select.Option value="LIVE">Live</Select.Option>
                  <Select.Option value="SIMULATION">Simulation</Select.Option>
                </Select>
              </Form.Item>
            </div>
            <div className={styles.filterActions}>
              <SubmitButton label="Filter" />
              <Button type="link" htmlType="button" onClick={clearFilters}>
                Clear
              </Button>
            </div>
          </Form>
        </div>
      </div>
      <div className={styles['startup-table']}>
        <>
          <Table
            className={styles['user-table']}
            dataSource={data?.adminGetAllUserRewards?.data}
            columns={columns}
            loading={loading}
            pagination={false}
            rowKey="id"
            scroll={{x: 'max-content'}}
          />
          <Pagination
            current={currentPage}
            onChange={e => handlePageChange(e)}
            total={data?.adminGetAllUserRewards?.pagination?.total}
            showQuickJumper
            className="ant-table-pagination"
            showSizeChanger
            pageSize={limit}
            onShowSizeChange={(_, size) => {
              handleLimitChange(size);
            }}
          />
        </>
      </div>

      <DBModal
        isOpen={openStatusModal}
        handleClose={() => setStatusModal(false)}
        content={
          <div>
            <h1>Update Status</h1>
            <Form
              className={styles.form}
              layout="vertical"
              onFinish={handleSave}>
              <div className={styles['date-format']}>
                <Form.Item
                  rules={[
                    {
                      required: true,
                      message: 'Status required',
                    },
                  ]}
                  name="status"
                  label="Status">
                  <Select style={{width: 340}} name="status">
                    {rewardStatus
                      .filter(item => item.key !== 'PENDING')
                      .map(({key, name}) => (
                        <Select.Option value={key} key={key}>
                          {name}
                        </Select.Option>
                      ))}
                  </Select>
                </Form.Item>
              </div>
              <SubmitButton disabled={loadingUpdate} label="Update" />
            </Form>
          </div>
        }
      />

      <DBModal
        isOpen={openReverseModal.open}
        handleClose={() => setOpenReverseModal({open: false, id: null})}
        content={
          <ReverseReward
            onClose={() => {
              setOpenReverseModal({open: false, id: null});
              reverseForm.resetFields();
            }}
            id={openReverseModal.id}
            form={reverseForm}
            refetch={refetch}
          />
        }
      />
    </div>
  );
};

export {AllUserReward};
